import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import DateFilter from "@/components/DateFilter";
import { ShieldCheck, RefreshCw, Download, Search } from "lucide-react";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const ACTION_LABELS = {
  "auth.admin_login":            "Signed in",
  "membership.extend":           "Extended membership",
  "membership.update":           "Updated membership",
  "membership.add_member":       "Added family member",
  "membership.remove_member":    "Removed family member",
  "verify.member":               "Verified member",
  "payout.create":               "Recorded payout",
  "payout.update":               "Updated payout",
  "payout.delete":               "Deleted payout",
  "payout.close_period":         "Closed payout period",
  "payout.bulk_import":          "Imported payouts (CSV)",
  "corporate.bulk_onboard":      "Onboarded corporate batch",
  "admin_user.create":           "Created admin user",
  "admin_user.update":           "Updated admin user",
  "admin_user.delete":           "Deleted admin user",
  "admin_user.password_reset":   "Reset admin password",
  "member.password_reset":       "Reset member password",
  "settings.update":             "Updated settings",
  "discount.extension_granted":  "Granted extension days",
  "kyc.verified":                "KYC verified",
  "kyc.rejected":                "KYC rejected",
  "kyc.pending":                 "KYC reset to pending",
  "role.create":                 "Created role",
  "role.update":                 "Updated role",
  "role.delete":                 "Deleted role",
  "employee.bulk_import":        "Imported employees (CSV)",
  "employee.create":             "Created employee",
  "employee.update":             "Updated employee",
  "employee.delete":             "Deleted employee",
  "plan.create":                 "Created plan",
  "plan.update":                 "Updated plan",
  "plan.delete":                 "Deleted plan",
  "membership.profile_update":   "Updated primary member",
  "membership.sub_member_update":"Updated family member",
  "membership.offline_enrol":    "Offline enrolment",
  "membership.customer_doc_update": "Member uploaded documents",
  "corporate_lead.create":      "Corporate enquiry received",
  "corporate_lead.update":      "Updated corporate lead",
  "lead.update":                "Updated lead",
  "invoice.resend":             "Resent invoice email",
};

const MODULE_LABELS = {
  auth: "Login / Logout",
  membership: "Membership",
  verify: "Verification",
  payout: "Payouts",
  corporate: "Corporate",
  discount: "Discount Codes",
  kyc: "KYC",
  admin_user: "Admin Users",
  member: "Member",
  settings: "Settings",
  invoice: "Invoices",
  role: "Roles",
  employee: "Employees",
  plan: "Plans",
  corporate_lead: "Corporate Leads",
  lead: "Leads",
  invoice: "Invoice Emails",
};

const ACTION_STYLES = {
  "auth.admin_login":       "bg-[#1F7A3D]/10 text-[#1F7A3D]",
  "membership.extend":      "bg-[#1F7A3D]/10 text-[#1F7A3D]",
  "membership.update":      "bg-[#1E4F87]/10 text-[#1E4F87]",
  "membership.add_member":  "bg-[#1E4F87]/10 text-[#1E4F87]",
  "verify.member":          "bg-[#C89B3C]/15 text-[#A0741F]",
  "payout.create":          "bg-[#A53A2A]/10 text-[#A53A2A]",
  "payout.update":          "bg-[#1E4F87]/10 text-[#1E4F87]",
  "payout.delete":          "bg-[#E63946]/10 text-[#E63946]",
  "payout.close_period":    "bg-[#0F2540]/10 text-[#0F2540]",
  "payout.bulk_import":     "bg-[#1E4F87]/10 text-[#1E4F87]",
  "corporate.bulk_onboard": "bg-[#1E4F87]/10 text-[#1E4F87]",
  "admin_user.create":      "bg-[#1F7A3D]/10 text-[#1F7A3D]",
  "admin_user.update":      "bg-[#1E4F87]/10 text-[#1E4F87]",
  "admin_user.delete":      "bg-[#E63946]/10 text-[#E63946]",
  "settings.update":        "bg-[#C89B3C]/15 text-[#A0741F]",
  "discount.extension_granted": "bg-[#1F7A3D]/10 text-[#1F7A3D]",
  "kyc.verified":           "bg-[#1F7A3D]/10 text-[#1F7A3D]",
  "kyc.rejected":           "bg-[#E63946]/10 text-[#E63946]",
  "kyc.pending":            "bg-[#C89B3C]/15 text-[#A0741F]",
  "employee.create":        "bg-[#1F7A3D]/10 text-[#1F7A3D]",
  "employee.update":        "bg-[#1E4F87]/10 text-[#1E4F87]",
  "employee.delete":        "bg-[#E63946]/10 text-[#E63946]",
  "plan.create":            "bg-[#1F7A3D]/10 text-[#1F7A3D]",
  "plan.update":            "bg-[#1E4F87]/10 text-[#1E4F87]",
  "plan.delete":            "bg-[#E63946]/10 text-[#E63946]",
  "membership.profile_update":   "bg-[#1E4F87]/10 text-[#1E4F87]",
  "membership.sub_member_update":"bg-[#1E4F87]/10 text-[#1E4F87]",
  "membership.offline_enrol":    "bg-[#1F7A3D]/10 text-[#1F7A3D]",
  "membership.customer_doc_update": "bg-[#C89B3C]/15 text-[#A0741F]",
  "corporate_lead.create":  "bg-[#1F7A3D]/10 text-[#1F7A3D]",
  "corporate_lead.update":  "bg-[#1E4F87]/10 text-[#1E4F87]",
  "lead.update":            "bg-[#1E4F87]/10 text-[#1E4F87]",
  "invoice.resend":         "bg-[#1E4F87]/10 text-[#1E4F87]",
};

const MODULES = [
  { value: "", label: "All modules" },
  { value: "auth", label: "Login / Logout" },
  { value: "membership", label: "Membership" },
  { value: "verify", label: "Verification" },
  { value: "payout", label: "Payouts" },
  { value: "corporate", label: "Corporate" },
  { value: "discount", label: "Discount / Codes" },
  { value: "kyc", label: "KYC" },
  { value: "admin_user", label: "Admin Users" },
  { value: "settings", label: "Settings" },
  { value: "invoice", label: "Invoices" },
  { value: "employee", label: "Employees" },
  { value: "plan", label: "Plans" },
  { value: "lead", label: "Leads" },
  { value: "corporate_lead", label: "Corporate Leads" },
];

const ACTIONS = [
  { value: "", label: "All actions" },
  { value: "auth.admin_login", label: "Login" },
  { value: "membership.extend", label: "Membership extend" },
  { value: "membership.update", label: "Membership update" },
  { value: "membership.add_member", label: "Family member added" },
  { value: "verify.member", label: "Verify member" },
  { value: "kyc.verified", label: "KYC verified" },
  { value: "kyc.rejected", label: "KYC rejected" },
  { value: "kyc.pending", label: "KYC reset to pending" },
  { value: "payout.create", label: "Payout create" },
  { value: "payout.update", label: "Payout update" },
  { value: "payout.delete", label: "Payout delete" },
  { value: "payout.close_period", label: "Payout close period" },
  { value: "payout.bulk_import", label: "Payout bulk import" },
  { value: "corporate.bulk_onboard", label: "Corporate bulk onboard" },
  { value: "discount.extension_granted", label: "Discount extension granted" },
  { value: "admin_user.create", label: "Admin user create" },
  { value: "admin_user.update", label: "Admin user update" },
  { value: "admin_user.delete", label: "Admin user delete" },
  { value: "settings.update", label: "Settings update" },
];

const ROLES = [
  { value: "", label: "All roles" },
  { value: "super_admin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "staff", label: "Staff" },
  { value: "accounts", label: "Accounts" },
];

export default function AdminLogs() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 25 });
  const [actionFilter, setActionFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [dateFilter, setDateFilter] = useState({ period: "", from_date: "", to_date: "" });
  const [busy, setBusy] = useState(false);

  const buildParams = (overrides = {}) => {
    const p = new URLSearchParams();
    if (actionFilter) p.set("action", actionFilter);
    if (moduleFilter && !actionFilter) p.set("module", moduleFilter);
    if (roleFilter) p.set("actor_role", roleFilter);
    if (actorFilter) p.set("actor_email", actorFilter);
    if (searchQ) p.set("q", searchQ);
    if (dateFilter.period) p.set("period", dateFilter.period);
    if (dateFilter.from_date) p.set("from_date", dateFilter.from_date);
    if (dateFilter.to_date) p.set("to_date", dateFilter.to_date);
    Object.entries(overrides).forEach(([k, v]) => p.set(k, v));
    return p;
  };

  const load = async (page = 1, limit = 25) => {
    setBusy(true);
    try {
      const params = buildParams({ page, limit });
      const r = await api.get(`/admin/logs?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load logs");
    } finally {
      setBusy(false);
    }
  };

  // Auto-refresh when any filter changes
  useEffect(() => { load(1, data.limit); /* eslint-disable-next-line */ }, [actionFilter, moduleFilter, roleFilter, actorFilter, dateFilter]);

  // Module → resets action so it doesn't conflict
  useEffect(() => { if (moduleFilter) setActionFilter(""); }, [moduleFilter]);
  useEffect(() => { if (actionFilter) setModuleFilter(""); }, [actionFilter]);

  const onSearch = (e) => {
    e.preventDefault();
    load(1, data.limit);
  };

  const onClear = () => {
    setActionFilter(""); setModuleFilter(""); setRoleFilter("");
    setActorFilter(""); setSearchQ("");
    setDateFilter({ period: "", from_date: "", to_date: "" });
  };

  const onExportCsv = async () => {
    try {
      const params = buildParams();
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
      const url = `${apiBase}/api/admin/logs/export.csv?${params.toString()}`;
      const token = localStorage.getItem("vip_token");
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `activity-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click();
      URL.revokeObjectURL(a.href); a.remove();
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(e.message || "Could not export CSV");
    }
  };

  const filterCount = useMemo(() => {
    return [actionFilter, moduleFilter, roleFilter, actorFilter, searchQ,
      dateFilter.period, dateFilter.from_date, dateFilter.to_date].filter(Boolean).length;
  }, [actionFilter, moduleFilter, roleFilter, actorFilter, searchQ, dateFilter]);

  return (
    <div className="space-y-6" data-testid="admin-logs-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl font-bold text-[#0F2540] flex items-center gap-2">
            <ShieldCheck className="h-6 w-6" /> Activity Logs
          </h2>
          <p className="text-sm text-[#5C6B7A] mt-1">
            Audit trail for admin-initiated actions — login, members, payouts, KYC, settings, discounts and more.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={onExportCsv} className="btn-ghost !text-xs" data-testid="logs-export-csv">
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
          <button onClick={() => load(data.page, data.limit)} className="btn-ghost !text-xs" data-testid="logs-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-[#E1E8F0] bg-white overflow-hidden">
        <div className="p-4 border-b border-[#E1E8F0] space-y-3">
          <DateFilter value={dateFilter} onChange={setDateFilter} testid="logs-date-filter" />
          <div className="flex flex-wrap gap-3">
            <select
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              className="rounded-xl border border-[#E1E8F0] px-3 py-2 text-sm"
              data-testid="logs-module-filter"
            >
              {MODULES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="rounded-xl border border-[#E1E8F0] px-3 py-2 text-sm"
              data-testid="logs-action-filter"
            >
              {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="rounded-xl border border-[#E1E8F0] px-3 py-2 text-sm"
              data-testid="logs-role-filter"
            >
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <input
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
              placeholder="Filter by admin email"
              className="flex-1 min-w-[200px] rounded-xl border border-[#E1E8F0] px-3 py-2 text-sm"
              data-testid="logs-actor-filter"
            />
            <form onSubmit={onSearch} className="flex-1 min-w-[220px] flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C6B7A]" />
                <input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Search target, member id, code, reason..."
                  className="w-full pl-9 pr-3 py-2 rounded-xl border border-[#E1E8F0] text-sm"
                  data-testid="logs-search-input"
                />
              </div>
              <button type="submit" className="btn-ghost !text-xs" data-testid="logs-search-btn">Search</button>
            </form>
            {filterCount > 0 && (
              <button onClick={onClear} className="text-xs text-[#5C6B7A] underline" data-testid="logs-clear-filters">
                Clear filters ({filterCount})
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F7FB] text-[#5C6B7A] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Activity</th>
                <th className="text-left px-4 py-3">Target</th>
                <th className="text-left px-4 py-3">By</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => {
                const mod = (row.action || "").split(".", 1)[0] || "—";
                const actionLabel = ACTION_LABELS[row.action] || (row.action || "").replace(/^[^.]+\./, "").replace(/_/g, " ");
                const story = humanizePayload(row.action, row.payload, row.actor_name || row.actor_email);
                const actorDisplay = row.actor_username || row.actor_name || (row.actor_email || "").split("@")[0] || "system";
                return (
                  <tr key={row.log_id} className="border-t border-[#E1E8F0] hover:bg-[#F4F7FB]/40 align-top" data-testid={`log-${row.log_id}`}>
                    <td className="px-4 py-3 text-xs text-[#5C6B7A] whitespace-nowrap">{fmtDateTime(row.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-bold ${ACTION_STYLES[row.action] || "bg-[#5C6B7A]/10 text-[#5C6B7A]"}`}>
                          {actionLabel}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-[#5C6B7A]">{MODULE_LABELS[mod] || mod}</span>
                      </div>
                      {story && (
                        <div className="mt-1.5 text-sm text-[#0F2540] leading-snug" data-testid={`log-story-${row.log_id}`}>{story}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-[#5C6B7A] whitespace-nowrap">{row.target || "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      <div className="text-[#0F2540] font-semibold">{actorDisplay}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[#5C6B7A]">{row.actor_role || "—"}</div>
                      {row.actor_email && row.actor_email !== actorDisplay && (
                        <div className="text-[10px] text-[#5C6B7A]">{row.actor_email}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {data.items.length === 0 && !busy && (
                <tr><td colSpan={4} className="px-4 py-12 text-center text-sm text-[#5C6B7A]" data-testid="logs-empty">No activity matches your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="p-3 border-t border-[#E1E8F0]">
          <Pagination
            page={data.page} limit={data.limit} total={data.total}
            onPageChange={(p) => load(p, data.limit)}
            onLimitChange={(l) => load(1, l)}
            testid="admin-logs-pagination"
          />
        </div>
      </div>
    </div>
  );
}

function humanizePayload(action, payload, actorLabel) {
  if (!payload || typeof payload !== "object") return "";
  const who = actorLabel || "Someone";
  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  // CHANGE diffs — `changes` may be either:
  //   • an Array of field names (legacy)              → just lists which fields changed
  //   • an Object map { field: newValue }             → shows "field → newValue"
  // When `previous` map is also supplied → shows "field: old → new" form for max clarity.
  const fmtVal = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "object") return JSON.stringify(v).slice(0, 80);
    const s = String(v);
    return s.length > 40 ? s.slice(0, 38) + "…" : s;
  };
  const labelize = (k) => k
    .replace(/_doc_id$/, " document")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const renderChanges = (changes, previous = {}) => {
    if (!changes) return "";
    const keys = Array.isArray(changes) ? changes : Object.keys(changes);
    if (!keys.length) return "";
    const isMap = !Array.isArray(changes);
    const parts = keys.slice(0, 4).map((k) => {
      if (k === "password_changed") return "password changed";
      const oldV = (previous || {})[k];
      const newV = isMap ? changes[k] : undefined;
      const oldS = fmtVal(oldV);
      const newS = fmtVal(newV);
      if (oldV !== undefined && newV !== undefined && oldS !== newS) {
        return `${labelize(k)}: ${oldS} → ${newS}`;
      }
      if (newV !== undefined) return `${labelize(k)} → ${newS}`;
      if (oldV !== undefined) return `${labelize(k)} (was ${oldS})`;
      return labelize(k);
    });
    const more = keys.length - parts.length;
    return parts.join(" · ") + (more > 0 ? ` (+${more} more)` : "");
  };

  switch (action) {
    case "auth.admin_login":
      return `${who} signed in${payload.ip ? ` from ${payload.ip}` : ""}.`;
    case "membership.extend":
      return `${who} extended this membership by +${payload.extend_days} day${(payload.extend_days || 0) === 1 ? "" : "s"}${payload.reason ? `. Reason: ${payload.reason}` : ""}.`;
    case "membership.update": {
      const detail = renderChanges(payload.new || payload.changes, payload.previous);
      return detail
        ? `${who} updated the membership — ${detail}.`
        : `${who} updated the membership.`;
    }
    case "membership.add_member":
      return `${who} added ${payload.member_name || "a member"}${payload.relation ? ` (${payload.relation})` : ""} to the membership.`;
    case "membership.remove_member":
      return `${who} removed ${payload.member_name || "a member"}${payload.relation ? ` (${payload.relation})` : ""} from the membership.`;
    case "verify.member":
      return payload.matched
        ? `${who} verified the member via ${payload.matched_via || payload.method || "lookup"}.`
        : `${who} attempted verification — no match found.`;
    case "kyc.verified":
      return `${who} marked KYC as verified${payload.remarks ? ` (${payload.remarks})` : ""}.`;
    case "kyc.rejected":
      return `${who} rejected KYC${payload.remarks ? ` — reason: ${payload.remarks}` : ""}.`;
    case "kyc.pending":
      return `${who} reset KYC to pending${payload.remarks ? ` (${payload.remarks})` : ""}.`;
    case "payout.create":
      return `${who} recorded a payout of ${inr(payload.amount)} to ${payload.employee_name || payload.employee_id || "an employee"}${payload.method ? ` via ${payload.method.replace("_", " ")}` : ""}.`;
    case "payout.update": {
      const detail = renderChanges(payload.new || payload.changes, payload.previous);
      return detail
        ? `${who} updated payout — ${detail}.`
        : `${who} updated a payout${payload.amount ? ` (${inr(payload.amount)})` : ""}.`;
    }
    case "payout.delete":
      return `${who} deleted a payout${payload.amount ? ` of ${inr(payload.amount)}` : ""}${payload.employee_name ? ` for ${payload.employee_name}` : ""}.`;
    case "payout.close_period":
      return `${who} closed the payout period${payload.period ? ` ${payload.period}` : ""}${payload.locked_count ? ` (${payload.locked_count} entries locked)` : ""}.`;
    case "payout.bulk_import":
      return `${who} imported ${payload.created || 0} payouts${payload.skipped ? `, skipped ${payload.skipped}` : ""} via CSV.`;
    case "corporate.bulk_onboard":
      return `${who} onboarded ${payload.created || 0} members${payload.skipped ? `, skipped ${payload.skipped}` : ""}${payload.company_name ? ` for ${payload.company_name}` : ""}.`;
    case "admin_user.create":
      return `${who} created a new ${payload.role || "admin"} user${payload.name ? ` for ${payload.name}` : ""}${payload.email ? ` (${payload.email})` : ""}.`;
    case "admin_user.update": {
      const detail = renderChanges(payload.new || payload.changes, payload.previous);
      return `${who} updated ${payload.target_email || "an admin user"}${detail ? ` — ${detail}` : ""}.`;
    }
    case "admin_user.delete":
      return `${who} deleted admin user ${payload.email || ""} (${payload.role || "?"}).`;
    case "admin_user.password_reset":
      return `${who} reset the password for ${payload.target_email || "an admin user"} (${payload.role || "?"}).`;
    case "member.password_reset":
      return `${who} reset the password for member ${payload.target_email || payload.member_id || ""}.`;
    case "settings.update":
      return `${who} updated settings: ${(payload.keys || []).join(", ") || "—"}.`;
    case "discount.extension_granted":
      return `${who}: discount code ${payload.code || "?"} granted +${payload.days_added} day${payload.days_added === 1 ? "" : "s"} to ${payload.extended_member_name || payload.extended_member_id || "a member"} (${payload.source || "online"}).`;
    case "role.create":
      return `${who} created a new role "${payload.name || payload.display_name || "?"}" with ${(payload.permissions || []).length} permission(s).`;
    case "role.update": {
      const detail = renderChanges(payload.new || payload.changes, payload.previous);
      return `${who} updated role ${payload.name || ""}${detail ? ` — ${detail}` : ""}.`;
    }
    case "role.delete":
      return `${who} deleted role "${payload.name || ""}".`;
    case "employee.bulk_import":
      return `${who} imported ${payload.created || 0} employees${payload.skipped ? `, skipped ${payload.skipped}` : ""} via CSV.`;
    case "employee.create":
      return `${who} created employee ${payload.name || ""}${payload.employee_code ? ` (${payload.employee_code})` : ""}${payload.email ? ` — ${payload.email}` : ""}.`;
    case "employee.update": {
      const detail = renderChanges(payload.new || payload.changes, payload.previous);
      return `${who} updated an employee${detail ? ` — ${detail}` : ""}.`;
    }
    case "employee.delete":
      return `${who} deleted employee ${payload.name || ""}${payload.employee_code ? ` (${payload.employee_code})` : ""}.`;
    case "plan.create":
      return `${who} created plan "${payload.name || ""}"${payload.price_amount ? ` at ${inr(payload.price_amount)}` : ""}.`;
    case "plan.update": {
      const detail = renderChanges(payload.new || payload.changes, payload.previous);
      return `${who} updated a plan${detail ? ` — ${detail}` : ""}.`;
    }
    case "plan.delete":
      return `${who} deleted a plan.`;
    case "membership.profile_update": {
      const detail = renderChanges(payload.new || payload.changes, payload.previous);
      return detail
        ? `${who} updated the primary member — ${detail}.`
        : `${who} updated the primary member's profile.`;
    }
    case "membership.sub_member_update": {
      const detail = renderChanges(payload.new || payload.changes, payload.previous);
      return detail
        ? `${who} updated a family member — ${detail}.`
        : `${who} updated a family member.`;
    }
    case "membership.offline_enrol":
      return `${who} created offline enrolment for ${payload.customer_email || "a customer"}${payload.plan_name ? ` on "${payload.plan_name}"` : ""}${payload.amount ? ` (${inr(payload.amount)})` : ""}${payload.method ? ` via ${payload.method}` : ""}.`;
    case "membership.customer_doc_update":
      return `${who} uploaded / updated KYC documents${payload.changed_fields?.length ? ` (${payload.changed_fields.join(", ")})` : ""}.`;
    case "corporate_lead.create":
      return `${who} submitted a corporate enquiry from "${payload.company || "an organisation"}"${payload.employee_count ? ` for ${payload.employee_count} employees` : ""}.`;
    case "corporate_lead.update": {
      const detail = renderChanges(payload.new || payload.changes, payload.previous);
      return `${who} updated corporate lead${payload.company ? ` "${payload.company}"` : ""}${detail ? ` — ${detail}` : ""}.`;
    }
    case "lead.update": {
      const detail = renderChanges(payload.new || payload.changes, payload.previous);
      const note = payload.note_added ? " · added a note" : "";
      return `${who} updated lead${payload.lead_name ? ` "${payload.lead_name}"` : ""}${detail ? ` — ${detail}` : ""}${note}.`;
    }
    case "invoice.resend":
      return `${who} resent invoice${payload.invoice_no ? ` ${payload.invoice_no}` : ""} to ${payload.customer_email || "the customer"}${payload.force ? " (forced)" : ""}.`;
    default: {
      const detail = renderChanges(payload.new || payload.changes, payload.previous);
      if (detail) return `${who}: ${detail}.`;
      const compact = JSON.stringify(payload);
      return compact.length > 140 ? compact.slice(0, 137) + "…" : compact;
    }
  }
}
